// eslint-disable-next-line no-restricted-imports
import { matchPath } from 'react-router';
import { enableSignedSAMLAuthnRequests } from '@gonfalon/dogfood-flags/dogfood-flags';
import http, { jsonToImmutable, jsonToImmutableError } from '@gonfalon/legacy-http-client/httpUtils';
import invariant from 'tiny-invariant';

import { getDestinationUrl } from '#actions/navigation';
import type { MFARecoveryCodes } from '#utils/accounts/records/Security';
import { createMFARecoveryCodes } from '#utils/accounts/records/Security';
import type { SignupForm } from '#utils/accounts/records/SignupForm';
import type { LoginInfo } from '#utils/loginUtils';
import type { ForgotPassword, ResetPassword } from '#utils/resetPasswordUtils';

export function login(info: LoginInfo | SignupForm) {
  return http
    .put('/internal/account/login', {
      body: info,
    })
    .catch(jsonToImmutableError);
}

export function loginV2(info: LoginInfo, redirectPath?: string) {
  const { username, password, accountId, oauth } = info;
  const convertUsernameToEmail = {
    email: username,
    password,
    accountId,
    oauth,
  };
  const body = enableSignedSAMLAuthnRequests()
    ? { ...convertUsernameToEmail, redirect: redirectPath || getDestinationUrl() }
    : convertUsernameToEmail;
  return http
    .post('/internal/account/login', {
      body,
    })
    .catch(jsonToImmutableError);
}

export function getLoginPage(email: string, redirectPath?: string) {
  const body: { email: string; redirect?: string } = { email };

  body.redirect = redirectPath || getDestinationUrl();

  return http
    .put('/internal/account/login2', {
      body,
    })
    .catch((err) => jsonToImmutableError(err));
}

export function escalateSession(info: LoginInfo) {
  return http
    .post('/internal/account/session/escalate', {
      body: info,
    })
    .catch(jsonToImmutableError);
}

export function loginWithMFA(token: string, passcode: string, accountID?: string) {
  return http
    .post('/internal/account/session/mfa', {
      body: { accountID, token, passcode }, // *Account ID is only required for multi-account members.
    })
    .catch(jsonToImmutableError);
}

export function confirmEnrollmentMFA(token: string, passcode: string): Promise<MFARecoveryCodes> {
  return http
    .post('/internal/login/mfa/confirm', {
      body: { token, passcode },
    })
    .then(jsonToImmutable)
    .then(createMFARecoveryCodes)
    .catch(jsonToImmutableError);
}

export function loginWithMFARecovery(token: string, recoveryCode: string, accountID?: string) {
  return http
    .post('/internal/account/session/mfa-recovery', {
      body: { accountID, token, recoveryCode },
    })
    .catch(jsonToImmutableError);
}

export function forgotPassword(reset: ForgotPassword) {
  return http
    .post('/internal/forgot', {
      body: JSON.stringify(reset.toJS()),
    })
    .catch(jsonToImmutableError);
}

export function resetPassword(reset: ResetPassword) {
  const match = matchPath('/reset/:passwordResetToken', window.location.pathname);
  invariant(match?.params.passwordResetToken, 'Expected passwordResetToken route param');

  return http
    .post(`/internal/reset/${match.params.passwordResetToken}`, {
      body: JSON.stringify(reset.toJS()),
    })
    .catch(jsonToImmutableError);
}

export function logout() {
  return http.delete('/internal/account/session').catch(jsonToImmutableError);
}
